import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./App.css";
import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";
import { apolloClient } from "./client";
import { ApolloProvider, useQuery } from "@apollo/client";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./routes/helpers";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { ToastContainer } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider, Theme, useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ILinkButtonProps } from "./components/LinkButton";
import ResponsiveDrawer from "./components/ResponsiveDrawer";
import { GET_ME } from "./routes/users/graphql";
import { UserRole } from "./__generated__/graphql";
import React from "react";
import { parseBooleanEnvFlag } from "./utils/envFlags";
dayjs.extend(utc);
dayjs.extend(timezone);

const defaultTheme = createTheme();
const isAnalyticsEnabled = parseBooleanEnvFlag(
  process.env.REACT_APP_ENABLE_ANALYTICS
);

const FULLSCREEN_ROUTES = ["/map/print"];

function App() {
  const location = useLocation();
  const isFullscreen = FULLSCREEN_ROUTES.includes(location.pathname);

  return (
    <ThemeProvider theme={defaultTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ApolloProvider client={apolloClient}>
          <AuthProvider>
            {isFullscreen ? (
              <>
                <CssBaseline />
                <Outlet />
              </>
            ) : (
              <>
                <header className="App-links">
                  <ResponsiveDrawer>
                    <List component="nav">
                      <Routes />
                    </List>
                  </ResponsiveDrawer>
                </header>
                <CssBaseline />
                <Box
                  component="main"
                  className="main-background"
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light"
                        ? theme.palette.grey[100]
                        : theme.palette.grey[900],
                    flexGrow: 1,
                    height: "95vh",
                  }}
                >
                  <Outlet />
                </Box>
                <ToastContainer />
              </>
            )}
          </AuthProvider>
        </ApolloProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export const useIsSuperAdmin = () => {
  const auth = useAuth();
  const hasToken = Boolean(auth?.accessToken);
  const { data } = useQuery(GET_ME, { skip: !hasToken });

  const role = data?.me?.userRole;
  return role === UserRole.Superadmin;
};

export const useCurrentUserEmail = () => {
  const auth = useAuth();
  const hasToken = Boolean(auth?.accessToken);
  const { data } = useQuery(GET_ME, { skip: !hasToken });
  return data?.me?.email ?? null;
};

interface NavSection {
  title: string;
  items: ILinkButtonProps[];
  defaultOpen?: boolean;
  showHeader?: boolean;
  forceDesktopCollapse?: boolean;
}

const Routes = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const sections: NavSection[] = React.useMemo(() => {
    const base: NavSection[] = [
      {
        title: "News",
        items: [{ path: "/news", text: "News" }],
        showHeader: false,
      },
      {
        title: "Lineup",
        items: [
          { path: "/bands", text: "Bands" },
          { path: "/editions", text: "Festival Editions" },
          { path: "/stages", text: "Stages" },
          { path: "/showcase", text: "Showcase" },
        ],
      },
      {
        title: "Experience",
        items: [
          { path: "/vendors", text: "Vendors" },
          { path: "/drinks", text: "Drinks" },
          { path: "/information", text: "Info" },
          { path: "/sponsors", text: "Sponsors" },
          { path: "/polls", text: "Polls" },
          { path: "/map", text: "Map" },
          { path: "/map/interactive", text: "Interactive Map" },
          { path: "/areas", text: "Area capacity" },
        ],
      },
      {
        title: "Notifications",
        items: [
          { path: "/notification/history", text: "Notifications" },
        ],
        showHeader: false,
      },
      {
        title: "Admin",
        items: [
          ...(isAnalyticsEnabled ? [{ path: "/analytics", text: "Analytics" }] : []),
          { path: "/settings", text: "Settings" },
          { path: "/users", text: "Users" },
          { path: "/links", text: "Links" },
          { path: "/csv-import", text: "CSV Importer" },
        ],
      },
      {
        title: "Help",
        items: [{ path: "/help", text: "Help" }],
        showHeader: false,
      },
    ];

    if (isSuperAdmin) {
      base.push({
        title: "Super Admin",
        defaultOpen: false,
        forceDesktopCollapse: true,
        items: [
          { path: "/image-tools", text: "Image Tools (Admin)" },
          { path: "/theme-editor", text: "Theme Editor" },
          { path: "/data-importer", text: "Importer" },
        ],
      });
    }

    return base;
  }, [isSuperAdmin]);

  return (
    <>
      {sections.map((section) => (
        <NavigationSection key={section.title} {...section} />
      ))}
    </>
  );
};

const NavigationSection = ({
  title,
  items,
  defaultOpen = true,
  showHeader = true,
  forceDesktopCollapse = false,
}: NavSection) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const shouldForceDesktopOpen = showHeader && !forceDesktopCollapse && isLargeScreen;
  const computedDefaultOpen = shouldForceDesktopOpen ? true : defaultOpen;
  const [open, setOpen] = React.useState(computedDefaultOpen);
  const navigate = useNavigate();

  const headerTypographyProps = {
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1,
    sx: { textTransform: "uppercase", color: "text.secondary" },
  };

  const headerText = (
    <ListItemText primary={title} primaryTypographyProps={headerTypographyProps} />
  );

  React.useEffect(() => {
    if (!showHeader) return;
    setOpen(computedDefaultOpen);
  }, [computedDefaultOpen, showHeader]);

  const headerBaseSx = (theme: Theme, isActive: boolean) => ({
    px: 2,
    py: 1.25,
    mb: 0.5,
    borderRadius: 1,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: isActive
      ? theme.palette.action.selected
      : theme.palette.background.paper,
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest,
    }),
    boxShadow: isActive ? `inset 0 0 0 1px ${theme.palette.divider}` : "none",
  });

  const itemList = (
    <List
      component="div"
      disablePadding
      sx={(theme) => ({
        mb: 1,
        px: 0.5,
        borderLeft: `1px solid ${theme.palette.divider}`,
      })}
    >
      {items.map((item) => (
        <NavigationItem key={item.path} {...item} />
      ))}
    </List>
  );

  if (!showHeader) {
    return (
      <>
        {items.map((item) => (
          <ListItemButton
            key={item.path}
            onClick={() => navigate(item.path)}
            sx={(theme) => ({
              ...headerBaseSx(theme, false),
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            })}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={headerTypographyProps}
            />
          </ListItemButton>
        ))}
      </>
    );
  }

  return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        sx={(theme) => ({
          ...headerBaseSx(theme, open),
          "&:hover": {
            backgroundColor: open
              ? theme.palette.action.selected
              : theme.palette.action.hover,
          },
        })}
      >
        {headerText}
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {itemList}
      </Collapse>
    </>
  );
};

const NavigationItem = (props: ILinkButtonProps) => {
  const navigate = useNavigate();
  const handleClick = () => {
    navigate(props.path);
  };
  return (
    <ListItemButton
      onClick={handleClick}
      style={props.style}
      sx={(theme) => ({
        borderRadius: 1,
        ml: 1,
        mr: 1,
        mb: 0.5,
        pl: 4,
        pr: 2,
        color: theme.palette.text.secondary,
        transition: theme.transitions.create(["background-color", "color"], {
          duration: theme.transitions.duration.shorter,
        }),
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.text.primary,
        },
      })}
    >
      <ListItemText
        primary={props.text}
        primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
      />
    </ListItemButton>
  );
};

export default App;
